'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

type ChangedFieldPaths = ReadonlySet<string>;

const EMPTY_CHANGED_FIELD_PATHS: ChangedFieldPaths = new Set<string>();

const FormChangeHighlightContext = createContext<ChangedFieldPaths>(EMPTY_CHANGED_FIELD_PATHS);

export const changedFieldClassName = 'border-amber-400 bg-amber-50 text-amber-950 ring-1 ring-amber-200';
export const changedFieldContainerClassName = '-mx-2 rounded-md bg-amber-50 px-2 py-2 ring-1 ring-amber-200';

function fieldPathMatches(changedFieldPath: string, fieldPath: string): boolean {
  return (
    changedFieldPath === fieldPath ||
    changedFieldPath.startsWith(`${fieldPath}.`) ||
    fieldPath.startsWith(`${changedFieldPath}.`)
  );
}

export function FormChangeHighlightProvider({
  changedFieldPaths,
  children,
}: {
  changedFieldPaths?: ChangedFieldPaths | string[];
  children: ReactNode;
}) {
  const value = useMemo<ChangedFieldPaths>(() => {
    if (changedFieldPaths == null) {
      return EMPTY_CHANGED_FIELD_PATHS;
    }

    return changedFieldPaths instanceof Set ? changedFieldPaths : new Set(changedFieldPaths);
  }, [changedFieldPaths]);

  return <FormChangeHighlightContext.Provider value={value}>{children}</FormChangeHighlightContext.Provider>;
}

export function useIsChangedField(fieldPath?: string | null): boolean {
  const changedFieldPaths = useContext(FormChangeHighlightContext);

  if (!fieldPath || changedFieldPaths.size === 0) {
    return false;
  }

  return [...changedFieldPaths].some((changedFieldPath) => fieldPathMatches(changedFieldPath, fieldPath));
}
